import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'

import {
  expectGpuOk,
  gpuClient,
  type GpuSchemas,
  unwrapGpu,
} from '@/api/gpu/client'

import { GPU_KEY, type ProviderPatch } from './useGpuAdmin'

export function useGpuMutation<TArgs>(
  run: (args: TArgs) => Promise<unknown>,
  success: string,
) {
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void message.success(success)
      return queryClient.invalidateQueries({ queryKey: GPU_KEY })
    },
    onError: (error) => void message.error(error.message),
  })
}

export function usePatchGpuProvider() {
  return useGpuMutation(
    async ({ key, patch }: { key: string; patch: ProviderPatch }) =>
      unwrapGpu(
        await gpuClient.PATCH('/gpu/admin/v1/providers/{provider}', {
          params: { path: { provider: key } },
          body: patch,
        }),
        'Failed to update provider',
      ),
    'Provider updated',
  )
}

export function useTerminateGpuInstance() {
  return useGpuMutation(
    async ({ id, body }: { id: string; body: GpuSchemas['TerminateBody'] }) =>
      expectGpuOk(
        await gpuClient.POST('/gpu/admin/v1/instances/{id}/terminate', {
          params: { path: { id } },
          body,
        }),
        'Failed to terminate GPU instance',
      ),
    'Termination queued',
  )
}

export function usePatchGpuSettings() {
  return useGpuMutation(
    async (markupPercent: number | null) =>
      unwrapGpu(
        await gpuClient.PATCH('/gpu/admin/v1/settings', {
          body: { markup_percent: markupPercent },
        }),
        'Failed to update global markup',
      ),
    'Global markup updated',
  )
}
